import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Icon, toast } from "@sagot/ui";
import { useRef, useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import type { ServiceModuleId } from "../../../service/service-modules";
import type { WorkflowOut } from "../types";
import { KindBadge } from "../kind-badge";
import { useWorkflowMutations } from "../use-workflows";
import { DefaultStateBadge, ModifiedBadge, UpdateAvailableBadge } from "./default-badges";
import { DefaultDialogs, type DefaultDialog } from "./default-dialogs";
import { DefaultMenu } from "./default-menu";
import { ModuleOffNote } from "./module-off-note";
import { isOptional, statusOf, wdOf } from "./defaults-model";

const D = "engage.workflows.defaults";
const L = `${D}.service`;
/** The most steps a card names before it says there are more. */
const STEPS_SHOWN = 5;

interface DefaultCardProps {
  workflow: WorkflowOut;
  canManage: boolean;
  canPublish: boolean;
  duplicateLocked: boolean;
  /** The step types of the default as the catalogue ships it (what it does, in order); not given for one that was edited. */
  steps?: readonly string[];
  /** The Service module the default needs when it is switched off: the default is paused by that switch. */
  moduleOff?: ServiceModuleId | null;
}

/** "Route ticket → Notify team members": the steps of the shipped default, in the words of the builder. */
function StepsLine({ steps }: { steps: readonly string[] }) {
  const { t } = useTranslation();
  const named = steps.slice(0, STEPS_SHOWN).map((type) => t(`engage.workflows.steps.${type}`, { defaultValue: type }));
  const more = steps.length > STEPS_SHOWN ? " …" : "";
  return (
    <p className="text-xs text-fg-muted" data-testid="default-steps">
      <span className="font-semibold">{t(`${L}.stepsLabel`)}:</span> {named.join(" → ")}{more}
    </p>
  );
}

/** One default workflow: what it is called, what it does, whether it runs, and whether it was changed or has a newer revision. */
export function DefaultCard({ workflow, canManage, canPublish, duplicateLocked, steps, moduleOff = null }: DefaultCardProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { summary } = useWorkspaceContext();
  const { pause, resume } = useWorkflowMutations(workflow.id);
  const [dialog, setDialog] = useState<DefaultDialog | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  // A default the module switch paused is not "paused by somebody": it says so and has no Resume until the module is on.
  const blockedByModule = moduleOff !== null && workflow.state === "paused";
  const status = blockedByModule ? "module" : statusOf(workflow);
  const offByDefault = isOptional(workflow) && workflow.state !== "live";
  const wd = wdOf(workflow);
  const turnOn = () => {
    resume.mutate(undefined, {
      onSuccess: () => { toast.success(t(`${D}.${offByDefault ? "switchedOn" : "resumed"}`, { name: workflow.name })); },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  const confirmPause = () => {
    pause.mutate(undefined, {
      onSuccess: () => { setDialog(null); toast.info(t(`${D}.paused`, { name: workflow.name })); },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  return (
    <li className="card card-body flex flex-col gap-3" data-testid="default-card" data-key={workflow.system_key ?? undefined} data-status={status}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h4 className="text-sm font-bold">
            <AppLink className="text-primary-ink hover:underline" href={consoleRoutes.workflow(summary.slug, workflow.id)}>{workflow.name}</AppLink>
          </h4>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-fg-subtle">
            {wd ? <code className="font-mono font-semibold text-fg-muted" data-testid="default-wd" aria-label={t(`${L}.wdLabel`, { id: wd })}>{wd}</code> : null}
            <code className="min-w-0 break-all font-mono" data-testid="default-trigger" title={t(`${L}.triggerLabel`)}>{workflow.trigger}</code>
          </p>
        </div>
        <DefaultMenu workflow={workflow} canManage={canManage} canPublish={canPublish} duplicateLocked={duplicateLocked} blockedByModule={blockedByModule} triggerRef={trigger} onResume={turnOn} onOpenDialog={setDialog} />
      </div>
      {workflow.description ? <p className="text-sm text-fg-muted">{workflow.description}</p> : null}
      {steps && steps.length > 0 ? <StepsLine steps={steps} /> : null}
      <div className="flex flex-wrap items-center gap-2">
        <DefaultStateBadge status={status === "module" ? "paused" : status} />
        <KindBadge kind={workflow.kind} />
        <span className="text-xs text-fg-subtle" title={t(`${L}.kind.${workflow.kind === "facing" ? "facing" : workflow.execution}`)} data-testid="default-execution">{workflow.kind === "facing" ? "facing" : workflow.execution}</span>
        {workflow.is_modified ? <ModifiedBadge /> : null}
        {workflow.has_default_update ? <UpdateAvailableBadge /> : null}
      </div>
      {blockedByModule ? <ModuleOffNote module={moduleOff} /> : null}
      <div className="mt-auto flex items-center justify-between gap-2 pt-1">
        <span className="text-xs text-fg-subtle">{t(`${D}.runs`, { count: workflow.stats.sent })}</span>
        {offByDefault && canPublish && !blockedByModule ? (
          <Button size="sm" variant="secondary" loading={resume.isPending} aria-label={t(`${D}.switchOnLabel`, { name: workflow.name })} onClick={turnOn}>
            <Icon name="play" />
            {t(`${D}.switchOn`)}
          </Button>
        ) : null}
      </div>
      <DefaultDialogs workflow={workflow} open={dialog} opener={trigger} pausing={pause.isPending} onPause={confirmPause} onClose={() => { setDialog(null); }} />
    </li>
  );
}
